/**
 * identity-service calls of Settings → Staff / Roles / Keys for other apps / Change history
 * (T11-45, T11-46; design/07 §4.7, design/08 §1–2):
 *
 * | what | endpoint | permission |
 * |---|---|---|
 * | staff list · one · add (invitation e-mail — T01-18; or a temporary password once) | `GET/POST /v1/admin/staff`, `GET …/{id}` | staff.read / staff.manage (+ staff.roles) |
 * | send the invitation again | `POST …/{id}/invite` | staff.manage |
 * | change name / role / shops / on-off | `PATCH /v1/admin/staff/{id}` (If-Match version — T01-15) · `POST …/{id}/deactivate` | staff.manage (role: staff.roles) |
 * | two-step reset (owner) · PIN | `POST …/{id}/2fa/reset` · `POST …/{id}/reset-pin` | staff.manage |
 * | roles · permission list + system defaults | `GET/POST /v1/admin/roles`, `PATCH/DELETE …/{id}` (If-Match version), `GET …/permissions` | staff.read / staff.roles |
 * | built-in role back to default (T01-19) | `POST /v1/admin/roles/{id}/reset` | staff.roles |
 * | keys | `GET/POST /v1/admin/api-keys?includeRevoked=`, `DELETE …/{id}` (revoke) | api_keys.manage |
 * | change history · CSV | `GET /v1/admin/audit-logs?entity=&entityId=&actor=&actorType=&action=&from=&to=&cursor=` · `…/export.csv` | audit.view |
 */
import {
  createIdentityClient,
  type IdentityPaths,
  type ServiceClientOptions,
} from "@tcg/api-client";
import { useMemo } from "react";

import { unwrap } from "@/lib/api/errors";
import { useClientOptions } from "@/lib/api/use-api";

type Json<T> = T extends { content: { "application/json": infer B } } ? B : never;
type Body<T> = T extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : T extends { requestBody?: { content: { "application/json": infer B } } }
    ? B
    : never;
type P = IdentityPaths;

export type StaffPage = Json<P["/v1/admin/staff"]["get"]["responses"][200]>;
export type StaffRow = StaffPage["data"][number];
export type StaffCreateIn = Body<P["/v1/admin/staff"]["post"]>;
export type StaffCreated = Json<P["/v1/admin/staff"]["post"]["responses"][201]>;
export type StaffPatchIn = Body<P["/v1/admin/staff/{staff_id}"]["patch"]>;
export type RoleList = Json<P["/v1/admin/roles"]["get"]["responses"][200]>;
export type Role = RoleList["data"][number];
export type InviteSent = Json<P["/v1/admin/staff/{staff_id}/invite"]["post"]["responses"][200]>;
export type RoleCreateIn = Body<P["/v1/admin/roles"]["post"]>;
export type RolePatchIn = Body<P["/v1/admin/roles/{role_id}"]["patch"]>;
export type PermissionCatalog = Json<P["/v1/admin/roles/permissions"]["get"]["responses"][200]>;
export type ApiKeyPage = Json<P["/v1/admin/api-keys"]["get"]["responses"][200]>;
export type ApiKey = ApiKeyPage["data"][number];
export type ApiKeyCreateIn = Body<P["/v1/admin/api-keys"]["post"]>;
export type ApiKeyCreated = Json<P["/v1/admin/api-keys"]["post"]["responses"][201]>;
export type AuditPage = Json<P["/v1/admin/audit-logs"]["get"]["responses"][200]>;
export type AuditEntry = AuditPage["data"][number];

export interface AuditQuery {
  entity?: string;
  entityId?: string;
  actor?: string;
  actorType?: string;
  action?: string;
  service?: string;
  from?: string;
  to?: string;
  cursor?: string | null;
  limit?: number;
}

export interface StaffApi {
  staff(
    query: { q?: string; cursor?: string | null; limit?: number },
    signal?: AbortSignal,
  ): Promise<StaffPage>;
  createStaff(body: StaffCreateIn): Promise<StaffCreated>;
  /** `version` = the one the form was opened with → If-Match (409 `version_conflict` if stale). */
  patchStaff(id: string, body: StaffPatchIn, version?: number): Promise<StaffRow>;
  deactivate(id: string): Promise<unknown>;
  resetTwoStep(id: string): Promise<unknown>;
  /** T01-18: a fresh invitation e-mail (the old link and any temporary password stop working). */
  resendInvite(id: string): Promise<InviteSent>;
  roles(signal?: AbortSignal): Promise<Role[]>;
  /** The list + whether the built-in roles may be changed on this shop (T01-19). */
  roleList(signal?: AbortSignal): Promise<RoleList>;
  permissions(signal?: AbortSignal): Promise<PermissionCatalog>;
  createRole(body: RoleCreateIn): Promise<Role>;
  /** `version` = the role's version on screen → If-Match (409 `version_conflict` if stale). */
  patchRole(id: string, body: RolePatchIn, version?: number): Promise<Role>;
  /** T01-19: a built-in role back to its shipped permissions. */
  resetRole(id: string): Promise<Role>;
  deleteRole(id: string): Promise<unknown>;
  apiKeys(includeRevoked: boolean, signal?: AbortSignal): Promise<ApiKeyPage>;
  createKey(body: ApiKeyCreateIn): Promise<ApiKeyCreated>;
  revokeKey(id: string): Promise<ApiKey>;
  audit(query: AuditQuery, signal?: AbortSignal): Promise<AuditPage>;
  auditCsv(query: AuditQuery): Promise<Blob>;
}

function clean<T extends object>(query: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  ) as Partial<T>;
}

export function createStaffApi(options: ServiceClientOptions): StaffApi {
  const identity = createIdentityClient(options);
  return {
    staff: (query, signal) =>
      unwrap(
        identity.GET("/v1/admin/staff", {
          params: {
            query: { limit: query.limit ?? 100, ...clean({ q: query.q, cursor: query.cursor }) },
          },
          signal,
        }),
      ),
    createStaff: (body) => unwrap(identity.POST("/v1/admin/staff", { body })),
    patchStaff: (id, body, version) =>
      unwrap(
        identity.PATCH("/v1/admin/staff/{staff_id}", {
          params: {
            path: { staff_id: id },
            ...(version === undefined ? {} : { header: { "If-Match": `"${String(version)}"` } }),
          },
          body,
        }),
      ),
    deactivate: (id) =>
      unwrap(
        identity.POST("/v1/admin/staff/{staff_id}/deactivate", {
          params: { path: { staff_id: id } },
        }),
      ),
    resetTwoStep: (id) =>
      unwrap(
        identity.POST("/v1/admin/staff/{staff_id}/2fa/reset", {
          params: { path: { staff_id: id } },
        }),
      ),
    resendInvite: (id) =>
      unwrap(
        identity.POST("/v1/admin/staff/{staff_id}/invite", {
          params: { path: { staff_id: id } },
        }),
      ),
    roles: async (signal) => (await unwrap(identity.GET("/v1/admin/roles", { signal }))).data,
    roleList: (signal) => unwrap(identity.GET("/v1/admin/roles", { signal })),
    permissions: (signal) => unwrap(identity.GET("/v1/admin/roles/permissions", { signal })),
    createRole: (body) => unwrap(identity.POST("/v1/admin/roles", { body })),
    patchRole: (id, body, version) =>
      unwrap(
        identity.PATCH("/v1/admin/roles/{role_id}", {
          params: {
            path: { role_id: id },
            ...(version === undefined ? {} : { header: { "If-Match": `"${String(version)}"` } }),
          },
          body,
        }),
      ),
    resetRole: (id) =>
      unwrap(
        identity.POST("/v1/admin/roles/{role_id}/reset", { params: { path: { role_id: id } } }),
      ),
    deleteRole: (id) =>
      unwrap(identity.DELETE("/v1/admin/roles/{role_id}", { params: { path: { role_id: id } } })),
    apiKeys: (includeRevoked, signal) =>
      unwrap(
        identity.GET("/v1/admin/api-keys", {
          params: { query: { limit: 100, includeRevoked } },
          signal,
        }),
      ),
    createKey: (body) => unwrap(identity.POST("/v1/admin/api-keys", { body })),
    revokeKey: (id) =>
      unwrap(identity.DELETE("/v1/admin/api-keys/{key_id}", { params: { path: { key_id: id } } })),
    audit: (query, signal) =>
      unwrap(
        identity.GET("/v1/admin/audit-logs", {
          params: { query: { limit: query.limit ?? 50, ...clean({ ...query, limit: undefined }) } },
          signal,
        }),
      ),
    auditCsv: async (query) => {
      const fetcher = options.fetch ?? fetch;
      const search = new URLSearchParams(
        Object.entries(clean({ ...query, cursor: undefined, limit: undefined })) as Array<
          [string, string]
        >,
      );
      const token = await options.getAccessToken?.();
      const response = await fetcher(
        new Request(`${options.baseUrl}/v1/admin/audit-logs/export.csv?${search.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      );
      if (!response.ok) {
        throw new Error(`audit export ${String(response.status)}`);
      }
      return response.blob();
    },
  };
}

export function useStaffApi(): StaffApi {
  const options = useClientOptions();
  return useMemo(() => createStaffApi(options), [options]);
}

export const STAFF_KEY = ["settings", "staff"] as const;
export const ROLES_KEY = ["settings", "roles"] as const;
export const API_KEYS_KEY = ["settings", "api-keys"] as const;
export const AUDIT_KEY = ["settings", "audit"] as const;
